import React, { useEffect, useMemo, useState } from "react"
import { graphql, Link } from "gatsby"
import { CalendarIcon, CollectionIcon, TagIcon } from "@heroicons/react/outline"

import Layout from "../../components/promotions/layout"
import SEO from "../../components/common/seo"
import FaqSection from "../../components/home/FaqSection"
import PaymentLink from "../../components/promotions/paymentLink"
import PaidAdTopicPicker from "../../components/promotions/paidAdTopicPicker"
import withLocation from "../../components/common/withlocation"
import { getSchemaForWebPageWithFaq, HIVE_SITE } from "../../utils/schema"
import { paidAdPageFaq, faqToSectionProps } from "../../utils/staticPageFaq"

import {
  getLinkForTopicAd,
  getMonthlyPaidAdPriceForTopic,
  getSubscriptionLinkForTopicAd,
} from "../../utils/topicAdPrice"

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="h-6 w-5 flex-none text-rose-600"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12.75l6 6 9-13.5"
    />
  </svg>
)

const PaidAdPage = ({ data, path, queryParams }) => {
  const topics = useMemo(
    () =>
      (data?.paidAdTopics?.nodes || []).map(n => ({
        slug: n.slug,
        name: n.name,
        views: n.views,
      })),
    [data]
  )

  const [selectedSlug, setSelectedSlug] = useState(null)

  useEffect(() => {
    const raw = queryParams?.topic
    if (!raw || !topics.length) {
      return
    }
    const slug = decodeURIComponent(String(raw)).trim()
    if (topics.some(t => t.slug === slug)) {
      setSelectedSlug(slug)
    }
  }, [queryParams, topics])

  const selectedTopic = useMemo(
    () => (selectedSlug ? topics.find(t => t.slug === selectedSlug) : null),
    [topics, selectedSlug]
  )

  const displayPrice = getMonthlyPaidAdPriceForTopic(selectedTopic ?? null)
  /** Same dollars as the large headline; passed to picker so the closed label cannot drift. */
  const selectedRowPriceUsd = selectedTopic != null ? displayPrice : null
  const checkoutEnabled = Boolean(selectedSlug)
  const oneTimePaymentUrl = checkoutEnabled
    ? getLinkForTopicAd(selectedTopic ?? null)
    : ""
  const subscriptionPaymentUrl = checkoutEnabled
    ? getSubscriptionLinkForTopicAd(selectedTopic ?? null)
    : ""
  const paymentParamsBase = checkoutEnabled
    ? { utm_content: "paid_ad", topic: selectedSlug }
    : { utm_content: "paid_ad" }
  const subscriptionParamsBase = checkoutEnabled
    ? { utm_content: "paid_ad", topic: selectedSlug }
    : { utm_content: "paid_ad" }

  const faq = paidAdPageFaq()
  const pageSchema = getSchemaForWebPageWithFaq({
    pageUrl: `${HIVE_SITE}/partners/paid-ad/`,
    name: "Topic Page Ad | Hive Index",
    description:
      "Reach people browsing communities in a specific niche. Simple monthly placement on any topic page, starting at $50/mo.",
    faq,
    breadcrumbs: [
      { name: "Home", url: `${HIVE_SITE}/` },
      { name: "Partners", url: `${HIVE_SITE}/partners/` },
      { name: "Topic page ad", url: `${HIVE_SITE}/partners/paid-ad/` },
    ],
  })

  return (
    <Layout
      pageTitle="Advertise on a topic page"
      enableShare={true}
      includeBlurb={false}
      path={path}
    >
      <SEO
        title="Topic Page Ad | Hive Index"
        description="Reach people browsing communities in a specific niche. Simple monthly placement on any topic page, starting at $50/mo."
        path="/partners/paid-ad/"
        generateImage={true}
        schema={pageSchema}
      />

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex items-center justify-between">
          <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
            Why topic-page ads work
          </h2>
        </div>
      </div>

      <p className="mt-2 text-gray-500">
        Reach people browsing communities in a specific niche, from
        entrepreneurship and marketing to fitness, gaming, investing, and more.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3 text-sm text-gray-600">
        <div className="flex items-start gap-3">
          <TagIcon
            className="h-8 w-8 flex-shrink-0 text-rose-600"
            aria-hidden="true"
          />
          <div>
            <p className="font-semibold text-gray-900">Niche intent</p>
            <p className="mt-1">
              Your ad appears where people are already exploring a specific
              topic.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <CalendarIcon
            className="h-8 w-8 flex-shrink-0 text-rose-600"
            aria-hidden="true"
          />
          <div>
            <p className="font-semibold text-gray-900">
              Simple monthly placement
            </p>
            <p className="mt-1">
              Start with one topic page, then renew, switch topics, or subscribe
              for a permanent slot.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <CollectionIcon
            className="h-8 w-8 flex-shrink-0 text-rose-600"
            aria-hidden="true"
          />
          <div>
            <p className="font-semibold text-gray-900">Flexible offer types</p>
            <p className="mt-1">
              Link to a community, tool, newsletter, event, job board, course,
              or lead magnet.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 overflow-visible rounded-3xl ring-1 ring-gray-200 lg:mx-0 lg:flex lg:max-w-none">
        <div className="p-6 sm:p-8 lg:flex-auto">
          <h3 className="text-2xl font-bold tracking-tight text-gray-900">
            Topic Page Ad (1 month)
          </h3>
          <p className="mt-6 text-base leading-7 text-gray-600">
            Put a lightweight text/display ad on one topic page of your choice.
            It is a simple way to reach people already browsing communities in
            your category.
          </p>
          <p className="mt-2 text-base leading-7 text-gray-600">
            <Link
              to="/topics/"
              className="font-semibold underline text-rose-600"
            >
              Browse topic pages
            </Link>{" "}
            to see topic page viewership
          </p>

          <div className="mt-4 flex items-center gap-x-4">
            <h4 className="flex-none text-sm font-semibold leading-6 text-rose-600">
              Ad placement includes
            </h4>
            <div className="h-px flex-auto bg-gray-100" />
          </div>
          <ul
            role="list"
            className="mt-4 grid grid-cols-1 gap-4 text-sm leading-6 text-gray-600 sm:grid-cols-2 sm:gap-6"
          >
            <li className="flex gap-x-3">
              <CheckIcon />
              One topic page of your choice
            </li>
            <li className="flex gap-x-3">
              <CheckIcon />
              Placement on that topic page and any communities in that topic
            </li>
            <li className="flex gap-x-3">
              <CheckIcon />
              Link to your community, tool, newsletter, or offer
            </li>
            <li className="flex gap-x-3">
              <CheckIcon />
              Month-to-month, easy to pause or swap topics
            </li>
          </ul>
        </div>

        <div className="-mt-2 overflow-visible p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0 lg:min-w-0">
          <div className="overflow-visible rounded-2xl bg-yellow-50 py-10 text-center ring-1 ring-inset ring-yellow-100 lg:flex lg:flex-col lg:justify-center lg:py-10">
            <div className="mx-auto max-w-xs overflow-visible px-2 sm:px-3">
              <p className="text-base font-semibold text-gray-600">
                Topic page ad
              </p>
              <p className="mt-4 flex items-baseline justify-center gap-x-2">
                <span className="text-5xl font-bold tracking-tight text-gray-900 tabular-nums">
                  ${displayPrice}
                </span>
                <span className="text-sm font-semibold leading-6 tracking-wide text-gray-600">
                  /mo
                </span>
              </p>

              <PaidAdTopicPicker
                topics={topics}
                selectedSlug={selectedSlug}
                selectedRowPriceUsd={selectedRowPriceUsd}
                onSelect={setSelectedSlug}
              />

              <PaymentLink
                url={oneTimePaymentUrl}
                params={paymentParamsBase}
                title="Buy one month"
                additionalClassName="mt-6"
                disabled={!checkoutEnabled}
              />
              <PaymentLink
                url={subscriptionPaymentUrl}
                params={subscriptionParamsBase}
                title="Subscribe monthly"
                className="mt-3 block w-full rounded-md border border-rose-600 bg-white px-5 py-3 text-center cursor-pointer text-base font-semibold text-rose-600 shadow-sm hover:bg-rose-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600"
                disabled={!checkoutEnabled}
              />
              <p className="mt-5 text-sm leading-5 text-gray-600">
                We will follow up for the link and ad copy after checkout.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-8">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex items-center justify-between">
          <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
            How it works
          </h2>
        </div>
      </div>

      <div className="mt-4 text-gray-500">
        <ol className="list-decimal pl-6 space-y-1">
          <li>Pick a topic page</li>
          <li>Choose one month or a monthly subscription</li>
          <li>After checkout, we will follow up for your link and ad copy</li>
          <li>
            Your ad goes live in the topic-page sidebar and any communities in
            that topic
          </li>
        </ol>
      </div>

      <FaqSection faqs={faqToSectionProps(faq)} />
    </Layout>
  )
}

export const query = graphql`
  query PaidAdTopicsQuery {
    paidAdTopics: allGoogleSpreadsheetSourceTopics(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        views
      }
    }
  }
`

export default withLocation(PaidAdPage)
